import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';

import { WorkflowWebhookActionApiService } from '../services/workflow-webhook-action-api.service';
import { AuthService } from '../../../auth-pages/services/auth.service';
import {
  WorkflowWebhookEndpointResponse,
  WorkflowWebhookEndpointCreateRequest,
  WorkflowWebhookEndpointUpdateRequest
} from '../models/workflow-webhook-action.model';
import { environment } from '../../../../../environments/environment';
import { HasPermissionDirective } from '../../../../shared/directives/has-permission.directive';
import { DisableWithoutPermissionDirective } from '../../../../shared/directives/disable-without-permission.directive';
import { WorkflowResponse } from '../models/workflow.model';
import { WorkflowNodeResponse } from '../models/workflow-builder.model';
import { WorkflowApiService } from '../services/workflow-api.service';
import { ToastService } from '../../../../shared/services/toast.service';
import { workflowNodeSubtypeLabel } from '../models/workflow-labels';

@Component({
  selector: 'app-workflow-webhook-endpoints',
  standalone: true,
  imports: [CommonModule, RouterModule, FormsModule, HasPermissionDirective, DisableWithoutPermissionDirective],
  templateUrl: './workflow-webhook-endpoints.component.html'
})
export class WorkflowWebhookEndpointsComponent implements OnInit, OnDestroy {
  /** Nombre del nodo disparador cuando no tiene título: el desplegable salía VACÍO. */
  readonly nodeSubtypeText = workflowNodeSubtypeLabel;

  endpoints: WorkflowWebhookEndpointResponse[] = [];
  loading = false;
  error = '';

  // Form modal (create / edit)
  showFormModal = false;
  editingId: number | null = null;
  formLoading = false;
  formError = '';
  //
  workflows: WorkflowResponse[] = [];
  workflowsLoading = false;
  nodes: WorkflowNodeResponse[] = [];
  nodesLoading = false;

  fIntIdBussines: number | null = null;
  fIdWorkflow: number | null = null;
  fIdWorkflowVersion: number | null = null;
  fIdNode: number | null = null;
  fEndpointKey = '';
  fName = '';
  fDescription = '';
  fSecret = '';
  fIsActive = true;

  // Delete confirmation
  showDeleteModal = false;
  deletingEndpoint: WorkflowWebhookEndpointResponse | null = null;
  deleteLoading = false;
  deleteError = '';

  // Inbound test
  

  // Detail modal
  showDetailModal = false;
  detailEndpoint: WorkflowWebhookEndpointResponse | null = null;

  // Copy feedback
  copiedId: number | null = null;

  private destroy$ = new Subject<void>();

  constructor(
    private webhookApi: WorkflowWebhookActionApiService,
    private authService: AuthService,
    private workflowApi: WorkflowApiService,
    private toast: ToastService,
    private router: Router
  ) {}

  ngOnInit(): void { this.loadEndpoints(); }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private get businessId(): number {
    return this.authService.getBusinessId() ?? 0;
  }

  loadEndpoints(): void {
    this.loading = true;
    this.error = '';
    this.webhookApi.listEndpoints().pipe(takeUntil(this.destroy$)).subscribe({
      next: (list) => { this.endpoints = list; this.loading = false; },
      error: (err) => {
        this.error = err.error?.message || 'Error al cargar los endpoints.';
        this.loading = false;
      }
    });
  }

  openCreateModal(): void {
    this.editingId = null;
    this.fIntIdBussines = this.businessId;
    this.fIdWorkflow = null;
    this.fIdWorkflowVersion = null;
    this.fIdNode = null;
    this.fEndpointKey = '';
    this.fName = '';
    this.fDescription = '';
    this.fSecret = '';
    this.fIsActive = true;
    this.formError = '';
    this.nodes = [];
    this.showFormModal = true;
    this.loadWorkflows();
  }

  openEditModal(ep: WorkflowWebhookEndpointResponse): void {
    this.editingId = ep.id;
    this.fIntIdBussines = ep.intIdBussines;
    this.fIdWorkflow = ep.idWorkflow;
    this.fIdWorkflowVersion = ep.idWorkflowVersion ?? null;
    this.fIdNode = ep.idNode ?? null;
    this.fEndpointKey = ep.endpointKey;
    this.fName = ep.name ?? '';
    this.fDescription = ep.description ?? '';
    this.fSecret = '';
    this.fIsActive = ep.isActive;
    this.formError = '';
    this.nodes = [];
    this.showFormModal = true;
    this.loadWorkflows();
    if (this.fIdWorkflow) {
      this.loadNodes(this.fIdWorkflow);
    }
  }

  //metodos nuevos
  loadWorkflows(): void {
    this.workflowsLoading = true;
    this.workflowApi.getWorkflows({ businessId: this.businessId, size: 100 })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.workflows = Array.isArray(res) ? res : (res as any).content ?? [];
          this.workflowsLoading = false;
        },
        error: () => { this.workflowsLoading = false; }
      });
  }

  onWorkflowChange(): void {
    this.fIdNode = null;
    this.nodes = [];
    if (this.fIdWorkflow) {
      this.loadNodes(this.fIdWorkflow);
    }
  }

  loadNodes(workflowId: number): void {
    this.nodesLoading = true;
    this.workflowApi.listNodes(workflowId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (nodes) => { this.nodes = nodes; this.nodesLoading = false; },
      error: () => { this.nodes = []; this.nodesLoading = false; }
    });
  }

  confirmSave(): void {
    if (!this.fEndpointKey.trim()) { this.formError = 'Endpoint Key es obligatorio.'; return; }
    if (!this.fIdWorkflow) { this.formError = 'Debes seleccionar un workflow.'; return; }
    if (this.nodes.length > 0 && !this.fIdNode) {this.formError = 'Debes seleccionar un evento que active el workflow.';return;}
    if (!this.fIntIdBussines) { this.formError = 'Business ID es obligatorio.'; return; }

    this.formLoading = true;
    this.formError = '';

    if (this.editingId === null) {
      const req: WorkflowWebhookEndpointCreateRequest = {
        intIdBussines: this.fIntIdBussines!,
        idWorkflow: this.fIdWorkflow!,
        idWorkflowVersion: this.fIdWorkflowVersion ?? undefined,
        idNode: this.fIdNode ?? undefined,
        endpointKey: this.fEndpointKey.trim(),
        name: this.fName || undefined,
        description: this.fDescription || undefined,
        secret: this.fSecret || undefined,
        isActive: this.fIsActive
      };
      this.webhookApi.createEndpoint(req).pipe(takeUntil(this.destroy$)).subscribe({
        next: (ep) => {
          this.endpoints = [ep, ...this.endpoints];
          this.formLoading = false; this.showFormModal = false;
          this.toast.created('Endpoint', `Se creó el endpoint "${ep.endpointKey}".`);
        },
        error: (err) => { this.formError = err.error?.details || err.error?.message || 'Error al crear el endpoint.'; this.formLoading = false; }
      });
    } else {
      const req: WorkflowWebhookEndpointUpdateRequest = {
        idWorkflow: this.fIdWorkflow!,
        idWorkflowVersion: this.fIdWorkflowVersion ?? undefined,
        idNode: this.fIdNode ?? undefined,
        endpointKey: this.fEndpointKey.trim(),
        name: this.fName || undefined,
        description: this.fDescription || undefined,
        isActive: this.fIsActive
      };
      if (this.fSecret) req['secret'] = this.fSecret;
      this.webhookApi.updateEndpoint(this.editingId, req).pipe(takeUntil(this.destroy$)).subscribe({
        next: (ep) => {
          this.loadEndpoints();
          this.formLoading = false; this.showFormModal = false;
          this.toast.updated('Endpoint', `Se actualizó el endpoint "${ep.endpointKey}".`);
        },
        error: (err) => { this.formError = err.error?.details || err.error?.message|| 'Error al actualizar.'; this.formLoading = false; }
      });
    }
  }

  openDeleteModal(ep: WorkflowWebhookEndpointResponse): void {
    this.deletingEndpoint = ep;
    this.deleteError = '';
    this.showDeleteModal = true;
  }

  confirmDelete(): void {
    if (!this.deletingEndpoint) return;
    this.deleteLoading = true;
    this.deleteError = '';
    this.webhookApi.deleteEndpoint(this.deletingEndpoint.id).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.endpoints = this.endpoints.filter(e => e.id !== this.deletingEndpoint!.id);
        this.deleteLoading = false; this.showDeleteModal = false; this.deletingEndpoint = null;
        this.toast.deleted('Endpoint');
      },
      error: (err) => { this.deleteError = err.error?.details || err.error?.message  || 'Error al eliminar.'; this.deleteLoading = false; }
    });
  }

  getInboundUrl(ep: WorkflowWebhookEndpointResponse): string {
    return `${environment.apiUrl}api/workflow-webhooks/inbound/${ep.endpointKey}`;
  }

  async copyUrl(ep: WorkflowWebhookEndpointResponse): Promise<void> {
    try {
      await navigator.clipboard.writeText(this.getInboundUrl(ep));
      this.copiedId = ep.id;
      setTimeout(() => { if (this.copiedId === ep.id) this.copiedId = null; }, 2000);
    } catch {}
  }

  openTestInbound(ep: WorkflowWebhookEndpointResponse): void {
    this.router.navigate(['/automation/workflow-actions/webhook-test'], {
      queryParams: { endpointId: ep.id },
    });
  }

  

  toggleActive(ep: WorkflowWebhookEndpointResponse): void {
    this.webhookApi.updateEndpoint(ep.id, { isActive: !ep.isActive }).pipe(takeUntil(this.destroy$)).subscribe({
      next: (updated) => { this.endpoints = this.endpoints.map(e => e.id === updated.id ? updated : e); },
      error: (err) => { this.error = err.error?.message || 'Error al cambiar estado.'; }
    });
  }

  // Detail modal
  openDetailModal(ep: WorkflowWebhookEndpointResponse): void {
    this.detailEndpoint = ep;
    this.showDetailModal = true;
  }
}
